import React, { useEffect, useState } from 'react';
import { Container } from '../components/common';
import SpacedRepetitionMotivation from '../components/SpacedRepetitionMotivation';
import UserStats from '../components/UserStats';
import { userService } from '../api/services/user';
import StreakDisplay from '../components/StreakDisplay';
import { streakService } from '../api/services/streak';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalTimeSpent: '0h 0m',
    totalSessions: 0,
    averageAccuracy: 0,
    lastSessionAccuracy: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [streakData, setStreakData] = useState({
    currentStreak: 0,
    longestStreak: 0,
    lastPracticeDate: new Date().toISOString(),
    practiceHistory: [],
    streakFreezeAvailable: false,
  });
  const [isStreakLoading, setIsStreakLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const response = await userService.getUserProgress();

        if (!isMounted) return;

        if (response.data?.success && response.data.data) {
          const progress = response.data.data;
          setStats({
            totalTimeSpent: progress.total_time_spent,
            totalSessions: progress.total_sessions,
            averageAccuracy: progress.average_accuracy,
            lastSessionAccuracy: progress.last_session_accuracy,
          });
        } else {
          console.log('API response was not successful:', response);
        }
      } catch (error) {
        console.error('Failed to fetch user stats:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchStreakData = async () => {
      try {
        const [currentStreakResponse, historyResponse] = await Promise.all([
          streakService.getCurrentStreak(),
          streakService.getStreakHistory()
        ]);

        if (!isMounted) return;

        if (currentStreakResponse.success && historyResponse.success) {
          const streak = currentStreakResponse.data;
          const history = historyResponse.data;

          setStreakData({
            currentStreak: streak.current_streak,
            longestStreak: streak.longest_streak,
            lastPracticeDate: streak.last_practice_date,
            practiceHistory: history.practice_history || [],
            streakFreezeAvailable: streak.streak_freeze_available,
          });
        }
      } catch (error) {
        console.error('Failed to fetch streak data:', error);
      } finally {
        if (isMounted) {
          setIsStreakLoading(false);
        }
      }
    };

    fetchStats();
    fetchStreakData();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Container>
      <div className="py-8">
        <SpacedRepetitionMotivation />

        <div className="mt-8 mb-8">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <UserStats {...stats} isNewUser={stats.totalSessions === 0} currentStreak={streakData.currentStreak} />
          )}
        </div>

        <div className="mt-8 mb-8">
          {isStreakLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <StreakDisplay
              currentStreak={streakData.currentStreak}
              lastPracticeDate={streakData.lastPracticeDate}
              practiceHistory={streakData.practiceHistory}
            />
          )}
        </div>
      </div>
    </Container>
  );
};

export default Dashboard;